import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { timeAgo } from '../utils/dateUtils';
import {
  LayoutDashboard,
  Users,
  FileText,
  Briefcase,
  Receipt,
  UserCog,
  Package,
  Share2,
  BarChart3,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Bell,
  Mail,
  CheckCheck
} from 'lucide-react';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: [0, 5, 6] },
  { path: '/staff', icon: UserCog, label: 'User Accounts', roles: [0, 5] },
  { path: '/clients', icon: Users, label: 'Clients', roles: [0, 5, 6] },
  { path: '/quotes', icon: FileText, label: 'Quotes', roles: [0, 5, 6] },
  { path: '/jobs', icon: Briefcase, label: 'Job Sheets', roles: [0, 5, 6] },
  { path: '/invoices', icon: Receipt, label: 'Invoice', roles: [0, 5] },
  { path: '/items-services', icon: Package, label: 'Items and Services', roles: [0] },
  { path: '/referrals', icon: Share2, label: 'Referrals', roles: [0, 5] },
  { path: '/email-settings', icon: Mail, label: 'Email Setting', roles: [0, 5] },
  { path: '/reports', icon: BarChart3, label: 'Reports', roles: [0, 5] },
];

function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userTypeLabels = {
    0: 'God',
    5: 'Admin',
    6: 'Operations',
    1: 'Employee',
    2: 'Contractor',
    3: 'Sub Contractor'
  };

  const visibleNavItems = useMemo(
    () => navItems.filter(item => item.roles.includes(user?.user_type)),
    [user?.user_type]
  );

  return (
    <div className="flex h-screen bg-slate-100">
      {/* Sidebar */}
      <aside
        className={`no-print fixed inset-y-0 left-0 z-50 bg-sidebar transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700">
          <div className="flex items-center">
            <img 
              src="/logo.png" 
              alt="Project Maintenance Perth" 
              className={`${sidebarOpen ? 'h-12' : 'h-10'} w-auto object-contain`}
            />
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center px-4 py-3 mb-1 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary text-white'
                    : 'text-slate-400 hover:bg-sidebar-hover hover:text-white'
                }`
              }
            >
              <item.icon size={20} />
              {sidebarOpen && <span className="ml-3 text-sm">{item.label}</span>}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 print:!ml-0 ${
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
        }`}
      >
        {/* Header */}
        <header className="no-print h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </button>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:block p-2 text-slate-600 hover:text-slate-900"
              data-testid="sidebar-toggle-btn"
            >
              <Menu size={24} />
            </button>
          </div>

          <div className="flex items-center gap-4">
            <NotificationBell />

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100"
                data-testid="user-menu-btn"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.first_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-slate-700">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {userTypeLabels[user?.user_type] || 'User'}
                  </p>
                </div>
                <ChevronDown size={16} className="text-slate-400" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                  <button
                    onClick={() => { navigate('/profile'); setUserMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                    data-testid="my-profile-btn"
                  >
                    <Users size={16} />
                    My Profile
                  </button>
                  <div className="border-t border-slate-100 my-1" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                    data-testid="logout-btn"
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get('/api/notifications').then(r => r.data),
    refetchInterval: 60_000,           // poll every 60s instead of 15s
    refetchIntervalInBackground: false, // don't poll when tab hidden
    staleTime: 30_000,
  });

  const markRead = useMutation({
    mutationFn: (id) => api.put(`/api/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllRead = useMutation({
    mutationFn: () => api.put('/api/notifications/read-all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const unread = data?.unread_count || 0;
  const items = data?.items || [];

  const handleClick = (n) => {
    if (!n.is_read) markRead.mutate(n.id);
    if (n.quote_id) { navigate(`/quotes/${n.quote_id}`); setOpen(false); }
  };

  // timeAgo imported from utils/dateUtils

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="p-2 text-slate-500 hover:text-slate-700 relative" data-testid="notifications-btn">
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1" data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border z-50 overflow-hidden" data-testid="notifications-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-800">Notifications</h3>
            {unread > 0 && (
              <button onClick={() => markAllRead.mutate()} className="flex items-center gap-1 text-xs text-[#f4931d] hover:underline" data-testid="mark-all-read-btn">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No notifications</div>
            ) : items.map(n => (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                className={`px-4 py-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 transition-colors ${!n.is_read ? 'bg-orange-50/40' : ''}`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    n.type === 'quote_accepted' ? 'bg-green-100 text-green-600' :
                    n.type === 'quote_followup' ? 'bg-amber-100 text-amber-600' :
                    'bg-red-100 text-red-600'
                  }`}>
                    <FileText size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800 truncate">{n.title}</span>
                      {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#f4931d] shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                    <span className="text-[10px] text-slate-400 mt-1 block">{timeAgo(n.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Layout;
